import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { queryClient } from "./lib/queryClient";
import { AuthProvider } from "./context/AuthContext";
import AppShell from "./layouts/AppShell";
// Eager: entry/auth pages + short-link critical path (fast first paint)
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Forgot from "./pages/Forgot";
import ResetPassword from "./pages/ResetPassword";
import Redirector from "./pages/Redirector";
import PublicResolver from "./pages/PublicResolver";
import PromoBanner from "./components/playful/PromoBanner";
import CheckoutResume from "./components/CheckoutResume";
import Unlock from "./pages/Unlock";
import Blocked from "./pages/Blocked";
import Challenge from "./pages/Challenge";
import { BLOG, BLOG_BY_SLUG } from "./content/blog";
import { GUIDES, GUIDES_BY_SLUG } from "./content/guides";
import { COMPARE, COMPARE_BY_SLUG } from "./content/compare";
import { ALTERNATIVES, ALTERNATIVES_BY_SLUG } from "./content/alternatives";
import { USECASES, USECASES_BY_SLUG } from "./content/useCases";
import AnalyticsPageview from "./components/AnalyticsPageview";
import CookieConsent from "./components/CookieConsent";
import ScrollHelper from "./components/ScrollHelper";
import HostSEO from "./components/HostSEO";
import HostGuard from "./components/HostGuard";
import { Analytics as VercelAnalytics } from "@vercel/analytics/react";

// Lazy: authed app, content, and secondary marketing — split out of the
// initial bundle (pulls their heavy deps like recharts/EditLinkModal too).
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Links = lazy(() => import("./pages/Links"));
const Analytics = lazy(() => import("./pages/Analytics"));
const AnalyticsHome = lazy(() => import("./pages/AnalyticsHome"));
const Settings = lazy(() => import("./pages/Settings"));
const Pricing = lazy(() => import("./pages/Pricing"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const Domains = lazy(() => import("./pages/Domains"));
const Campaigns = lazy(() => import("./pages/Campaigns"));
const CampaignDetail = lazy(() => import("./pages/CampaignDetail"));
const Bulk = lazy(() => import("./pages/Bulk"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const ApiKeys = lazy(() => import("./pages/ApiKeys"));
const Webhooks = lazy(() => import("./pages/Webhooks"));
const Team = lazy(() => import("./pages/Team"));
const TeamAccept = lazy(() => import("./pages/TeamAccept"));
const Integrations = lazy(() => import("./pages/Integrations"));
const ContentIndex = lazy(() => import("./pages/ContentIndex"));
const WhatIsLinkLane = lazy(() => import("./pages/WhatIsLinkLane"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const DiscoverEnterprise = lazy(() => import("./pages/DiscoverEnterprise"));
const Customers = lazy(() => import("./pages/Customers"));
const Careers = lazy(() => import("./pages/Careers"));
const Developers = lazy(() => import("./pages/Developers"));
const Status = lazy(() => import("./pages/Status"));
const ContentArticle = lazy(() => import("./pages/ContentArticle"));
const Billing = lazy(() => import("./pages/Billing"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Security = lazy(() => import("./pages/Security"));
const Subprocessors = lazy(() => import("./pages/Subprocessors"));
const BioEditor = lazy(() => import("./pages/BioEditor"));
const PublicStats = lazy(() => import("./pages/PublicStats"));

// Legacy bio URL → apex handle (linklane.io/{slug}, beacons-style).
function BioRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/${slug}`} replace />;
}

function RouteFallback() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center" data-testid="route-fallback">
      <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-muted-foreground">
        LOADING…
      </div>
    </div>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-paper font-sans text-ink">
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <HostSEO />
            <HostGuard />
            <AnalyticsPageview />
            <VercelAnalytics />
            <CookieConsent />
            <ScrollHelper />
            <CheckoutResume />
            <PromoBanner />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                {/* ── Marketing / public ─────────────────── */}
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/forgot" element={<Forgot />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/onboarding" element={<Onboarding />} />
                {/* Product docs moved to docs.linklane.io — /docs and /docs/*
                    are 301-redirected at the edge (see frontend/vercel.json). */}
                <Route path="/what-is-linklane" element={<WhatIsLinkLane />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/contact/discover-enterprise" element={<DiscoverEnterprise />} />
                <Route path="/customers" element={<Customers />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/developers" element={<Developers />} />
                <Route path="/status" element={<Status />} />

                {/* ── Content marketing (SEO/GEO) ──────────── */}
                <Route
                  path="/blog"
                  element={
                    <ContentIndex
                      registry={BLOG}
                      basePath="/blog"
                      eyebrow="BLOG"
                      title="The LinkLane blog"
                      intro="Tactics and playbooks for link management, analytics, QR, bio pages, and growth."
                      tone="candy"
                    />
                  }
                />
                <Route
                  path="/blog/:slug"
                  element={
                    <ContentArticle
                      bySlug={BLOG_BY_SLUG}
                      basePath="/blog"
                      sectionName="Blog"
                      ldType="BlogPosting"
                    />
                  }
                />
                <Route
                  path="/guides"
                  element={
                    <ContentIndex
                      registry={GUIDES}
                      basePath="/guides"
                      eyebrow="GUIDES"
                      title="Complete guides"
                      intro="Deep, evergreen guides to the concepts behind modern link management."
                      tone="mint"
                    />
                  }
                />
                <Route
                  path="/guides/:slug"
                  element={
                    <ContentArticle
                      bySlug={GUIDES_BY_SLUG}
                      basePath="/guides"
                      sectionName="Guides"
                      ldType="Article"
                    />
                  }
                />
                <Route
                  path="/compare"
                  element={
                    <ContentIndex
                      registry={COMPARE}
                      basePath="/compare"
                      eyebrow="COMPARE"
                      title="LinkLane vs. the alternatives"
                      intro="Honest, feature-by-feature comparisons with the tools you're weighing."
                      tone="pink"
                    />
                  }
                />
                <Route
                  path="/compare/:slug"
                  element={
                    <ContentArticle
                      bySlug={COMPARE_BY_SLUG}
                      basePath="/compare"
                      sectionName="Compare"
                      ldType="Article"
                    />
                  }
                />
                <Route
                  path="/alternatives"
                  element={
                    <ContentIndex
                      registry={ALTERNATIVES}
                      basePath="/alternatives"
                      eyebrow="ALTERNATIVES"
                      title="Best alternatives, ranked"
                      intro="Researched roundups of the top tools in each category — and where LinkLane fits."
                      tone="yellow"
                    />
                  }
                />
                <Route
                  path="/alternatives/:slug"
                  element={
                    <ContentArticle
                      bySlug={ALTERNATIVES_BY_SLUG}
                      basePath="/alternatives"
                      sectionName="Alternatives"
                      ldType="Article"
                    />
                  }
                />
                <Route
                  path="/use-cases"
                  element={
                    <ContentIndex
                      registry={USECASES}
                      basePath="/use-cases"
                      eyebrow="USE CASES"
                      title="Built for your workflow"
                      intro="How agencies, creators, SaaS teams, and event marketers use LinkLane."
                      tone="candy"
                    />
                  }
                />
                <Route
                  path="/use-cases/:slug"
                  element={
                    <ContentArticle
                      bySlug={USECASES_BY_SLUG}
                      basePath="/use-cases"
                      sectionName="Use cases"
                      ldType="Article"
                    />
                  }
                />

                {/* ── Public pages (navbar, no sidebar) ───── */}
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/security" element={<Security />} />
                <Route path="/subprocessors" element={<Subprocessors />} />

                {/* ── Authenticated app shell ─────────────── */}
                <Route element={<AppShell />}>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/links" element={<Links />} />
                  <Route path="/analytics" element={<AnalyticsHome />} />
                  <Route path="/analytics/:id" element={<Analytics />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/campaigns" element={<Campaigns />} />
                  <Route path="/campaigns/:id" element={<CampaignDetail />} />
                  <Route path="/bulk" element={<Bulk />} />
                  <Route path="/domains" element={<Domains />} />
                  <Route path="/api-keys" element={<ApiKeys />} />
                  <Route path="/webhooks" element={<Webhooks />} />
                  <Route path="/team" element={<Team />} />
                  <Route path="/team/accept" element={<TeamAccept />} />
                  <Route path="/integrations" element={<Integrations />} />
                  <Route path="/billing" element={<Billing />} />
                  {/* Conventional alias → canonical /billing (no duplicate page) */}
                  <Route path="/settings/billing" element={<Navigate to="/billing" replace />} />
                  <Route path="/bio/edit" element={<BioEditor />} />
                </Route>

                {/* ── Redirect helpers / catch-all ────────── */}
                <Route path="/unlock/:code" element={<Unlock />} />
                <Route path="/blocked/:code" element={<Blocked />} />
                <Route path="/challenge/:code" element={<Challenge />} />
                {/* Legacy bio path → apex handle (linklane.io/{slug}) */}
                <Route path="/bio/:slug" element={<BioRedirect />} />
                <Route path="/s/:token" element={<PublicStats />} />
                {/* Apex catch-all: bio page (marketing host) or short link (short host) */}
                <Route path="/:code" element={<PublicResolver />} />
              </Routes>
            </Suspense>
          </AuthProvider>
        </BrowserRouter>
        {process.env.NODE_ENV === "development" && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            borderRadius: "16px",
            border: "2px solid #1E293B",
            background: "#FFFFFF",
            color: "#1E293B",
            fontFamily: "'Instrument Sans', sans-serif",
            fontWeight: 600,
            fontSize: "14px",
            boxShadow: "4px 4px 0 0 #1E293B",
          },
        }}
      />
    </div>
  );
}

export default App;
